import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, RefreshCw, ShieldCheck } from 'lucide-react';
import { api, dateTime } from '../lib/api';
import ProfilesStaffRiskAssessment from '../components/ProfilesStaffRiskAssessment';

// CRM-only staff review. The age assurance evidence can ONLY come from an
// independent provider attestation; staff can review creator eligibility but
// cannot set the age-verified flag or bypass server-side approval requirements.
export default function ProfilesCreatorReviews() {
  const [reviews,setReviews]=useState([]);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState('');
  const [notice,setNotice]=useState('');
  const [customerNumber,setCustomerNumber]=useState('');
  const [reason,setReason]=useState('');
  const [pending,setPending]=useState('');
  const [decisionReasons,setDecisionReasons]=useState({});
  const [history,setHistory]=useState({});
  const [loadingHistory,setLoadingHistory]=useState('');
  const [governance,setGovernance]=useState(null);
  const [governanceError,setGovernanceError]=useState('');
  const [governanceReason,setGovernanceReason]=useState('');
  const [governanceBusy,setGovernanceBusy]=useState(false);
  const [textListings,setTextListings]=useState([]);
  const [textLoading,setTextLoading]=useState(true);
  const [textSubmissionsOpen,setTextSubmissionsOpen]=useState(false);
  const [textError,setTextError]=useState('');
  const [textUpdatedAt,setTextUpdatedAt]=useState(null);
  const [textBusy,setTextBusy]=useState('');
  const [textReasons,setTextReasons]=useState({});

  const loadTextListings=useCallback(async()=>{
    setTextLoading(true);
    setTextError('');
    try {
      const result=await api('/api/profiles/text-directory');
      if(!result || result.success!==true || !Array.isArray(result.entries) ||
         result.directoryScope!=='non_explicit_text_only' ||
         result.submissionsOpen!==true)
        throw new Error('Back Office could not confirm that text-only submissions are open.');
      setTextListings(result.entries);
      setTextSubmissionsOpen(true);
      setTextUpdatedAt(new Date().toISOString());
    }catch(error){
      // A failed read must NEVER appear as a truthful 'No requests yet' state.
      setTextListings([]);
      setTextSubmissionsOpen(false);
      setTextUpdatedAt(null);
      setTextError(error?.message||'Unable to load listing requests.');
    }finally{setTextLoading(false);}
  },[]);

  async function decideTextListing(row,decision){
    const key=row.customer_number+'-'+row.profile_id;
    const reason=String(textReasons[key]||'').trim();
    if(reason.length<10){setError('Enter a moderation reason of at least 10 characters.');return;}
    setTextBusy(key);setError('');setNotice('');
    try{
      await api('/api/profiles/text-directory/'+encodeURIComponent(row.profile_id)+'/decision',{
        method:'POST',headers:{'content-type':'application/json'},
        body:JSON.stringify({customer_number:row.customer_number,decision,reason,
          public_name:row.public_name,public_summary:row.public_summary||''}),
      });
      setNotice('Non-explicit directory decision recorded. Adult-content emergency controls remain unchanged.');
      await loadTextListings();
    }catch(error){setError(error?.message||'Unable to record listing decision.');}
    finally{setTextBusy('');}
  }


  async function loadGovernance(){
    try{
      const result=await api('/api/profiles/governance');
      setGovernance(result);
      setGovernanceError('');
    }catch(error){
      setGovernance(null);
      setGovernanceError(error?.message||'Profiles release controls are unavailable.');
    }
  }
  async function changeGovernance(suspend){
    if(governanceBusy)return;
    const reason=governanceReason.trim();
    if(reason.length<12){
      setGovernanceError('Record an audit reason of at least 12 characters.');
      return;
    }
    setGovernanceBusy(true);setGovernanceError('');
    try{
      const response=await api('/api/profiles/governance',{
        method:'POST',headers:{'content-type':'application/json'},
        body:JSON.stringify({creator_eligibility_suspended:suspend,reason}),
      });
      setGovernance(current=>({...current,...response}));
      setGovernanceReason('');
      await loadGovernance();
      setNotice(suspend?'Creator publishing eligibility is suspended by CRM.':'Governed creator eligibility has been reopened.');
    }catch(error){setGovernanceError(error?.message||'Could not update Profiles release controls.');}
    finally{setGovernanceBusy(false);}
  }

  const load=useCallback(async ()=>{
    setLoading(true);setError('');
    try {
      const result=await api('/api/profiles/creator-reviews');
      setReviews(Array.isArray(result?.reviews)?result.reviews:[]);
    } catch(e) {setReviews([]);setError(e?.message||'This restricted CRM area is unavailable.');}
    finally {setLoading(false);}
  },[]);
  useEffect(()=>{void load();void loadGovernance();void loadTextListings();},[load,loadTextListings]);

  useEffect(()=>{
    // Staff often keep this desktop screen open while testing the Studio
    // submission in a separate browser. Recheck on returning to the app,
    // and periodically while it remains visible.
    const refreshWhenVisible=()=>{
      if(!document.hidden)void loadTextListings();
    };
    window.addEventListener('focus',refreshWhenVisible);
    document.addEventListener('visibilitychange',refreshWhenVisible);
    const timer=window.setInterval(refreshWhenVisible,30000);
    return ()=>{
      window.removeEventListener('focus',refreshWhenVisible);
      document.removeEventListener('visibilitychange',refreshWhenVisible);
      window.clearInterval(timer);
    };
  },[loadTextListings]);

  async function openReview(event){
    event.preventDefault();
    setError('');setNotice('');setPending('opening');
    try {
      await api('/api/profiles/creator-reviews',{method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify({
        customer_number:customerNumber.trim(),reason:reason.trim(),
      })});
      setCustomerNumber('');setReason('');
      setNotice('Creator review created in CRM. The account holder must complete independent age assurance before approval is possible.');
      await load();
    } catch(e){setError(e?.message||'Unable to open a creator review.');}
    finally{setPending('');}
  }

  async function showAudit(row){
    if(history[row.customer_number]) {
      setHistory(current=>({...current,[row.customer_number]:null}));return;
    }
    setError('');setLoadingHistory(row.customer_number);
    try {
      const result=await api('/api/profiles/creator-reviews/'+encodeURIComponent(row.customer_number)+'/events');
      setHistory(current=>({...current,[row.customer_number]:Array.isArray(result?.events)?result.events:[]}));
    } catch(e){setError(e?.message||'Creator review audit history is unavailable.');}
    finally{setLoadingHistory('');}
  }

  async function decide(row,decision){
    const explanation=String(decisionReasons[row.customer_number]||'').trim();
    if(explanation.length<10){setError('Enter an audit reason of at least 10 characters before recording a decision.');return;}
    setPending(row.customer_number);setError('');setNotice('');
    try {
      await api('/api/profiles/creator-reviews/'+encodeURIComponent(row.customer_number)+'/decision',{
        method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify({decision,reason:explanation}),
      });
      setDecisionReasons(current=>({...current,[row.customer_number]:''}));
      setNotice('CRM review decision recorded. Publisher eligibility automatically checks independent age assurance and approval.');
      await load();
    } catch(e){setError(e?.message||'The review decision could not be recorded.');}
    finally{setPending('');}
  }

  return <main className="profiles-admin-workspace">
    <ProfilesStaffRiskAssessment />
    <nav className="profiles-review-breadcrumb"><Link to="/profiles/admin"><ArrowLeft size={15}/> Profiles administration</Link><span aria-hidden="true">/</span><span>18+ listings and creator safeguards</span></nav>
    <header className="profiles-admin-workspace-header">
      <div><span className="profiles-admin-workspace-kicker">SM-CUSTOMER-OPERATIONS / BACK OFFICE</span>
        <h1 className="govuk-heading-xl">18+ Profiles — Publishing Control Centre</h1>
        <p className="govuk-body">Manage live age-restricted, non-explicit Profile names and introductions. Review and publish submitted listings here; explicit media remains a separate controlled feature.</p>
      </div>
      <button className="button secondary govuk-button govuk-button--secondary" type="button" onClick={()=>{void load();void loadTextListings();void loadGovernance();}} disabled={loading||textLoading}><RefreshCw size={15}/>Refresh all</button>
    </header>
    <p className="profiles-admin-legacy-notice govuk-body" role="status"><ShieldCheck size={15}/>
      <strong>18+ Profiles publishing: {textLoading?'CHECKING':textSubmissionsOpen&&!textError?'LIVE — NON-EXPLICIT LISTINGS':'UNAVAILABLE — SERVICE CHECK FAILED'}</strong>
      {' '}· Personal, Professional and Organisation Profiles can request a verified-adult listing.
      Each listing becomes visible after its owner's age verification, Back Office approval and Profile publication.
    </p>
    <section className="profiles-admin-panel" aria-labelledby="text-only-directory-heading">
      <div className="profiles-admin-panel-heading"><div>
        <span>BACK OFFICE · NON-EXPLICIT DIRECTORY</span>
        <h2 className="govuk-heading-m" id="text-only-directory-heading">18+ Profile listing requests</h2>
        {textLoading?<p className="govuk-body" role="status">Checking text-only listing service…</p>:
          textSubmissionsOpen&&!textError?
            <p className="govuk-body" role="status"><strong>LIVE — accepting 18+ non-explicit Profile submissions and reviews</strong></p>:
            <p className="govuk-body" role="alert"><strong>UNAVAILABLE — text-only listing queue could not be confirmed</strong></p>}
        <p className="govuk-body">Approve the exact non-explicit display name and optional short introduction submitted by the owner.
           Approval here does not verify a person's age or authorise pornography, media,
           adult-platform links or creator publishing. The adult-content emergency stop stays in force.</p>
      </div>
      <button className="button secondary govuk-button govuk-button--secondary" type="button" onClick={()=>void loadTextListings()} disabled={textLoading}>
        <RefreshCw size={15}/>Refresh listing requests
      </button></div>
      {textError&&<p className="profiles-admin-error govuk-body" role="alert">
        Listing requests could not be loaded: {textError} Use Refresh listing requests to retry.
      </p>}
      {!textError && textUpdatedAt && <p className="profiles-admin-empty govuk-body" role="status">
        Last checked {dateTime(textUpdatedAt)}. New submissions refresh when you return to Back Office.
      </p>}
      {textLoading?<p className="profiles-admin-empty govuk-body" role="status">Loading public listing requests…</p>:
        textError?null:
        !textListings.length?<p className="profiles-admin-empty govuk-body">No public 18+ listing requests have reached Back Office.</p>:
        <div className="profiles-admin-table-scroll"><table className="profiles-admin-table govuk-table">
          <thead className="govuk-table__head"><tr className="govuk-table__row"><th className="govuk-table__header">Profile</th><th className="govuk-table__header">Customer</th><th className="govuk-table__header">Status</th><th className="govuk-table__header">Back Office moderation</th></tr></thead>
          <tbody className="govuk-table__body">{textListings.map(row=>{
            const key=row.customer_number+'-'+row.profile_id;
            return <tr className="govuk-table__row" key={key}>
              <td className="govuk-table__cell"><strong>{row.public_name}</strong>
                <small>#{row.profile_id} · {row.profile_type}</small>
                {row.public_summary && <p className="mt-2 text-sm govuk-body" style={{whiteSpace:'pre-wrap'}}>
                  <strong>Submitted short introduction:</strong><br/>{row.public_summary}
                </p>}</td>
              <td className="govuk-table__cell">{row.customer_number}</td>
              <td className="govuk-table__cell"><span className="profiles-admin-status">{row.decision}</span>
                <small>{dateTime(row.updated_at)}</small></td>
              <td className="govuk-table__cell">
                <label className="profiles-creator-reason govuk-label">Moderation reason
                  <textarea className="govuk-textarea" aria-label={'Non-explicit listing decision for '+key}
                    maxLength={1000} value={textReasons[key]||''}
                    placeholder="Confirm non-explicit content and give an audit reason"
                    onChange={event=>setTextReasons(current=>({...current,[key]:event.target.value}))}/>
                </label>
                <div className="profiles-admin-row-actions">
                  <button type="button" className="profiles-admin-action" disabled={Boolean(textBusy)}
                    onClick={()=>void decideTextListing(row,'approved')}>Approve name &amp; introduction</button>
                  <button type="button" className="profiles-admin-action" disabled={Boolean(textBusy)}
                    onClick={()=>void decideTextListing(row,'rejected')}>Reject</button>
                  <button type="button" className="profiles-admin-action" disabled={Boolean(textBusy)}
                    onClick={()=>void decideTextListing(row,'revoked')}>Revoke</button>
                </div>
              </td>
            </tr>;
          })}</tbody>
        </table></div>}
    </section>
    <section className="profiles-admin-panel" aria-labelledby="profiles-governance-heading">
      <div className="profiles-admin-panel-heading"><div>
        <span>EXPLICIT MEDIA · SEPARATE SAFETY CONTROL</span><h2 className="govuk-heading-m" id="profiles-governance-heading">Adult media and creator links</h2>
        <p className="govuk-body">The moderated 18+ Profile directory above is independent and can operate while explicit images, videos and creator-platform links remain unavailable. Explicit media requires documented age-assurance effectiveness and content-safety checks.</p>
      </div></div>
      {governanceError&&<p className="profiles-admin-error govuk-body" role="alert">{governanceError}</p>}
      {!governance?<p className="profiles-admin-empty govuk-body" role="status">Release control unavailable — treat restricted publishing as suspended.</p>:
      <div className="profiles-creator-form">
        <p className="text-sm govuk-body"><strong>Non-explicit 18+ Profile listings: </strong>{textSubmissionsOpen&&!textError?'LIVE — individually moderated':'Service status unavailable'}</p>
        <p className="text-sm govuk-body"><strong>Explicit adult images, videos and creator links: </strong>{governance.creator_eligibility_suspended?'NOT ENABLED — restricted media remains private':'ELIGIBLE FOR INDIVIDUAL APPROVAL — age and safety controls still apply'}</p>
        <p className="text-sm govuk-body"><strong>Age assurance and adult-media safety evidence: </strong>{governance.adult_content_safety_ready?'Recorded by operator':'Not confirmed — explicit media cannot be activated yet'}</p>
        <label className="govuk-label">Reason for governance change<textarea className="govuk-textarea" required minLength={12} maxLength={1000}
          value={governanceReason} onChange={event=>setGovernanceReason(event.target.value)}
          placeholder="Describe the operational safety decision and evidence"/></label>
        <div className="profiles-admin-row-actions">
          <button type="button" className="button secondary govuk-button govuk-button--secondary" disabled={governanceBusy||governance.creator_eligibility_suspended}
            onClick={()=>void changeGovernance(true)}>Suspend creator eligibility</button>
          <button type="button" className="button primary govuk-button"
            disabled={governanceBusy||!governance.creator_eligibility_suspended||!governance.adult_content_safety_ready}
            onClick={()=>void changeGovernance(false)}>Enable explicit media when safety-ready</button>
        </div>
        <div className="profiles-creator-audit" aria-label="Profiles governance change history">
          <strong>Change history</strong>
          {(governance.events||[]).length ? governance.events.slice(0,12).map(item=>
            <p className="govuk-body" key={item.event_id}><strong>{item.new_suspended?'Suspended':'Reopened'}</strong> · {dateTime(item.occurred_at)} · {item.actor_id}<small>{item.reason}</small></p>)
            :<p className="govuk-body">No governance changes recorded.</p>}
        </div>
      </div>}
    </section>
    {error&&<p className="profiles-admin-error govuk-body" role="alert">{error}</p>}
    {notice&&<p className="profiles-admin-notice govuk-body" role="status">{notice}</p>}
    <section className="profiles-admin-panel">
      <div className="profiles-admin-panel-heading"><div><span>CREATOR ELIGIBILITY</span><h2 className="govuk-heading-m">Start a CRM review</h2><p className="govuk-body">The applicant must already have a customer number. Opening a review never marks an account as age-verified.</p></div></div>
      <form className="profiles-creator-form" onSubmit={event=>void openReview(event)}>
        <label className="govuk-label">Customer number<input className="govuk-input" required maxLength={80} value={customerNumber} onChange={event=>setCustomerNumber(event.target.value)} placeholder="Existing Customer360 number"/></label>
        <label className="govuk-label">Review reason<textarea className="govuk-textarea" required minLength={8} maxLength={1000} value={reason} onChange={event=>setReason(event.target.value)} placeholder="Reason for adult creator eligibility review"/></label>
        <button className="button primary govuk-button" type="submit" disabled={Boolean(pending)}>Open review</button>
      </form>
    </section>
    <section className="profiles-admin-panel">
      <div className="profiles-admin-panel-heading"><div><span>CONTROLLED WORK QUEUE</span><h2 className="govuk-heading-m">Adult creator reviews</h2><p className="govuk-body">Review and revoke permissions centrally. Age assurance records cannot be changed here.</p></div></div>
      {loading?<p className="profiles-admin-empty govuk-body" role="status">Loading CRM reviews…</p>:!reviews.length?
      <p className="profiles-admin-empty govuk-body">No adult creator reviews have been opened.</p>:<div className="profiles-admin-table-scroll"><table className="profiles-admin-table govuk-table">
        <thead className="govuk-table__head"><tr className="govuk-table__row"><th className="govuk-table__header">Customer</th><th className="govuk-table__header">Age assurance</th><th className="govuk-table__header">Creator approval</th><th className="govuk-table__header">Last reviewed</th><th className="govuk-table__header">Governed decision</th></tr></thead>
        <tbody className="govuk-table__body">{reviews.map(row=>{
          const expiry=Date.parse(String(row.age_assurance_expires_at||''));
          const ageCurrent=row.age_assurance_status==='verified_18_plus'&&Number.isFinite(expiry)&&expiry>Date.now();
          const disabled=Boolean(pending)||(loading);
          return <tr className="govuk-table__row" key={row.customer_number}>
            <td className="govuk-table__cell"><strong>{row.display_name||'Customer'}</strong><small>{row.customer_number}</small>
              {row.latest_dedicated_profile_request&&<small className="mt-1 block font-semibold text-indigo-700">Separate 18+ Creator Link-in-Bio · {row.latest_dedicated_profile_request}</small>}
              {row.back_office_referral_id&&<Link className="profiles-admin-action mt-2 inline-flex" to={'/back-office/'+encodeURIComponent(row.back_office_referral_id)}>
                Open Back Office referral · {row.back_office_referral_reference||'View case'} ({row.back_office_referral_status||'open'})
              </Link>}
            </td>
            <td className="govuk-table__cell"><span className="profiles-admin-status">{row.age_assurance_status||'Not verified'}</span><small>{row.age_assurance_expires_at?'Expires '+dateTime(row.age_assurance_expires_at):'Provider assurance required'}</small></td>
            <td className="govuk-table__cell"><span className="profiles-admin-status">{row.creator_approval||'pending'}</span></td>
            <td className="govuk-table__cell">{row.creator_reviewed_at?dateTime(row.creator_reviewed_at):'Not reviewed'}</td>
            <td className="govuk-table__cell"><label className="profiles-creator-reason govuk-label">Recorded reason<textarea className="govuk-textarea" aria-label={'Decision reason for '+row.customer_number} value={decisionReasons[row.customer_number]||''} onChange={event=>setDecisionReasons(current=>({...current,[row.customer_number]:event.target.value}))} maxLength={1000} placeholder="Explain the decision"/></label>
              <div className="profiles-admin-row-actions">
                <button type="button" className="profiles-admin-action" disabled={disabled||!ageCurrent} title={!ageCurrent?'Independent 18+ age assurance required':'Approve eligible creator'} onClick={()=>void decide(row,'approved')}>Approve</button>
                <button type="button" className="profiles-admin-action" disabled={disabled} onClick={()=>void decide(row,'rejected')}>Reject</button>
                <button type="button" className="profiles-admin-action" disabled={disabled} onClick={()=>void decide(row,'revoked')}>Revoke</button>
                <button type="button" className="profiles-admin-action" disabled={Boolean(loadingHistory)} onClick={()=>void showAudit(row)}>{history[row.customer_number]?'Hide history':'Audit history'}</button>
              </div>
              {history[row.customer_number]&&<div className="profiles-creator-audit" aria-label={'Audit history for '+row.customer_number}>
                {history[row.customer_number].length?history[row.customer_number].map(event=><p className="govuk-body" key={event.event_id}>
                  <strong>{event.operation.replaceAll('_',' ')}</strong> · {dateTime(event.occurred_at)} · {event.actor_id}
                  <small>{event.reason||'No further details recorded'}</small>
                </p>):<p className="govuk-body">No review events recorded yet.</p>}
              </div>}
            </td>
          </tr>;
        })}</tbody>
      </table></div>}
    </section>
  </main>;
}
